import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useKmClient } from '../../../lib/useKmClient.js';
import { useKmQuery } from '../hooks/useKmQuery.js';
import { useFiltriEsperienze, nomeLeggibile } from '../hooks/useTassonomie.js';
import { useAuth as useRealAuth } from '../../../context/AuthContext';
import { canManageEsperienze } from '../../../lib/roles.js';
import ErrorDetails from '../components/ErrorDetails.jsx';
import { formatDataIt } from '../preavvisi.js';
import { etichettaUtilizzabile } from './EsperienzaDetailPage.jsx';

/* UC-KM-01 — lista delle schede esperienza (riscritta il 28/09).

   Prima: GET /esperienze senza parametri, poi una GET di dettaglio per
   ciascuna riga e filtri calcolati nel browser; mancavano settore/dominio di
   secondo livello, certificazioni, seniority, paginazione e ordinamento.

   Ora i filtri, la ricerca testuale, l'ordinamento per colonna e la
   paginazione sono passati al backend, che li supporta tutti
   (structured_query.lista_esperienze):
     settore_l1/l2 e dominio_l1/l2 (codici della tassonomia), cliente,
     periodo (dal/al, sovrapposizione), tecnologie, certificazioni, stato,
     seniority, cerca; ordina/direzione; limite/offset.
   I valori delle tendine arrivano da GET /esperienze/filtri. I conteggi
   esposti sono quelli del backend (totale_complessivo, conteggi per stato).
   Il settore mostrato è lo snapshot storico registrato sull'esperienza.
   Selezionando una riga si apre la scheda esperienza (UC-KM-02). */

const PAGINA = 25;

const FILTRI_VUOTI = {
  cerca: '', cliente: '', settore_l1: '', settore_l2: '', dominio_l1: '', dominio_l2: '',
  dal: '', al: '', tecnologie: '', certificazioni: '', stato: '', seniority: '',
};

const COLONNE = [
  ['progetto', 'Progetto'],
  ['cliente', 'Cliente finale'],
  ['consulente', 'Risorsa'],
  ['ruolo', 'Ruolo'],
  ['settore', 'Settore'],
  ['dominio', 'Dominio'],
  ['data_inizio', 'Periodo'],
  ['stato', 'Stato'],
  ['ultima_compilazione', 'Ultima compilazione'],
];

// Colonne con date: al primo clic si ordinano dalla più recente.
const COLONNE_DATA = ['data_inizio', 'ultima_compilazione'];

// Il backend restituisce «gg/mm/aaaa - Tipo» (es. «13/10/2026 - Chiusura»);
// senza tipo arriva «13/10/2026 - »: si toglie il separatore finale.
function testoUltimaCompilazione(v) {
  if (!v) return 'Mai compilata';
  return String(v).replace(/\s*-\s*$/, '');
}

function descriviFiltri(f, filtri) {
  const nome = (albero, cod) => {
    for (const l1 of albero || []) {
      if (l1.codice === cod) return l1.nome;
      const l2 = (l1.livello_2 || []).find((x) => x.codice === cod);
      if (l2) return l2.nome;
    }
    return cod;
  };
  const parti = [];
  if (f.cerca) parti.push(`testo «${f.cerca}»`);
  if (f.cliente) parti.push(`cliente «${f.cliente}»`);
  if (f.settore_l2 || f.settore_l1) parti.push(`settore ${nome(filtri?.settori, f.settore_l2 || f.settore_l1)}`);
  if (f.dominio_l2 || f.dominio_l1) parti.push(`dominio ${nome(filtri?.domini, f.dominio_l2 || f.dominio_l1)}`);
  if (f.dal || f.al) parti.push(`periodo ${f.dal ? formatDataIt(f.dal) : '…'} – ${f.al ? formatDataIt(f.al) : '…'}`);
  if (f.tecnologie) parti.push(`tecnologia ${f.tecnologie}`);
  if (f.certificazioni) parti.push(`certificazione ${f.certificazioni}`);
  if (f.stato) parti.push(f.stato === 'aperta' ? 'in corso' : 'chiuse');
  if (f.seniority) parti.push(`seniority ${f.seniority}`);
  return parti.join(', ');
}

export default function EsperienzeListPage() {
  const kmClient = useKmClient('km');
  // Ruolo vero da /users/me, non il simulatore KM (fissato ad admin dal
  // 28/09): la UI deve mostrare solo ciò che il ruolo reale consente.
  const { user: realUser } = useRealAuth();
  const role = realUser?.role;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const consulenteIdFiltro = searchParams.get('consulente_id') || '';
  const { filtri, stato: statoFiltri } = useFiltriEsperienze();

  const [f, setF] = useState(FILTRI_VUOTI);
  const [fApplicati, setFApplicati] = useState(FILTRI_VUOTI);
  // 15/10 — RF-KM-23 (AF §9.7): ordinamento predefinito per data dell'ultima
  // compilazione, dalla più recente; le esperienze mai compilate vanno in coda
  // (NULLS LAST lato backend, ORDINAMENTI['ultima_compilazione']).
  const [ordina, setOrdina] = useState('ultima_compilazione');
  const [direzione, setDirezione] = useState('desc');
  const [offset, setOffset] = useState(0);

  // I campi di testo si applicano con un breve ritardo, le tendine subito.
  useEffect(() => {
    const t = setTimeout(() => { setFApplicati(f); setOffset(0); }, 350);
    return () => clearTimeout(t);
  }, [f]);

  const chiave = JSON.stringify({ fApplicati, ordina, direzione, offset, consulenteIdFiltro });
  const { data, status, error, refetch } = useKmQuery(
    () => kmClient.get('/esperienze', {
      query: {
        ...Object.fromEntries(Object.entries(fApplicati).filter(([, v]) => v)),
        consulente_id: consulenteIdFiltro || undefined,
        ordina, direzione, limite: PAGINA, offset,
      },
    }),
    [chiave]
  );

  const righe = Array.isArray(data?.esperienze) ? data.esperienze : [];
  const totale = data?.totale_complessivo ?? righe.length;
  const conteggi = data?.conteggi || {};
  const descrizione = descriviFiltri(fApplicati, filtri);

  function aggiorna(campo, valore) {
    setF((prev) => {
      const next = { ...prev, [campo]: valore };
      if (campo === 'settore_l1') next.settore_l2 = '';
      if (campo === 'dominio_l1') next.dominio_l2 = '';
      return next;
    });
  }

  function ordinaPer(col) {
    if (ordina === col) setDirezione((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setOrdina(col); setDirezione(COLONNE_DATA.includes(col) ? 'desc' : 'asc'); }
    setOffset(0);
  }

  const settoreSel = (filtri?.settori || []).find((x) => x.codice === f.settore_l1);
  const dominioSel = (filtri?.domini || []).find((x) => x.codice === f.dominio_l1);
  const sel = { className: 'field-select', style: { maxWidth: 200 } };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-5)' }}>
        <h1 className="section-title" style={{ margin: 0 }}>Esperienze</h1>
        {canManageEsperienze(role) && (
          <Link to="/knowledge-management/esperienze/nuova" className="btn btn-primary">Apri nuova esperienza</Link>
        )}
      </div>

      {consulenteIdFiltro && (
        <div style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-ink-muted)' }}>
          Filtrate per consulente ({consulenteIdFiltro}) — <Link to="/knowledge-management/esperienze" className="btn-link">vedi tutte</Link>
        </div>
      )}

      <div className="card" style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="field-input" style={{ maxWidth: 260 }} placeholder="Cerca su progetto, cliente, risorsa, ruolo…" value={f.cerca} onChange={(e) => aggiorna('cerca', e.target.value)} />
        <input className="field-input" style={{ maxWidth: 200 }} placeholder="Cliente finale" list="km-clienti" value={f.cliente} onChange={(e) => aggiorna('cliente', e.target.value)} />
        <datalist id="km-clienti">{(filtri?.clienti || []).map((c) => <option key={c} value={c} />)}</datalist>

        <select {...sel} value={f.settore_l1} onChange={(e) => aggiorna('settore_l1', e.target.value)} disabled={statoFiltri !== 'ready'}>
          <option value="">Settore L1 (tutti)</option>
          {(filtri?.settori || []).map((x) => <option key={x.codice} value={x.codice}>{x.nome}</option>)}
        </select>
        <select {...sel} value={f.settore_l2} onChange={(e) => aggiorna('settore_l2', e.target.value)} disabled={!settoreSel}>
          <option value="">Settore L2 (tutti)</option>
          {(settoreSel?.livello_2 || []).map((x) => <option key={x.codice} value={x.codice}>{x.nome}</option>)}
        </select>
        <select {...sel} value={f.dominio_l1} onChange={(e) => aggiorna('dominio_l1', e.target.value)} disabled={statoFiltri !== 'ready'}>
          <option value="">Dominio L1 (tutti)</option>
          {(filtri?.domini || []).map((x) => <option key={x.codice} value={x.codice}>{x.nome}</option>)}
        </select>
        <select {...sel} value={f.dominio_l2} onChange={(e) => aggiorna('dominio_l2', e.target.value)} disabled={!dominioSel}>
          <option value="">Dominio L2 (tutti)</option>
          {(dominioSel?.livello_2 || []).map((x) => <option key={x.codice} value={x.codice}>{x.nome}</option>)}
        </select>

        <label style={{ fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', gap: 4 }}>
          Dal <input type="date" className="field-input" style={{ maxWidth: 150 }} value={f.dal} onChange={(e) => aggiorna('dal', e.target.value)} />
        </label>
        <label style={{ fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', gap: 4 }}>
          Al <input type="date" className="field-input" style={{ maxWidth: 150 }} value={f.al} onChange={(e) => aggiorna('al', e.target.value)} />
        </label>

        <select {...sel} value={f.tecnologie} onChange={(e) => aggiorna('tecnologie', e.target.value)}>
          <option value="">Tecnologia (tutte)</option>
          {(filtri?.tecnologie || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select {...sel} value={f.certificazioni} onChange={(e) => aggiorna('certificazioni', e.target.value)}>
          <option value="">Certificazione (tutte)</option>
          {(filtri?.certificazioni || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select {...sel} value={f.stato} onChange={(e) => aggiorna('stato', e.target.value)}>
          <option value="">Stato (tutti)</option>
          {/* 16/10 — FE-06: mai il codice E1/E2 a schermo, anche se arriva dal backend. */}
          {(filtri?.stati || [{ codice: 'aperta', nome: 'In corso' }, { codice: 'chiusa', nome: 'Chiusa' }]).map((x) => (
            <option key={x.codice} value={x.codice}>{String(x.nome || '').replace(/^E\d+\s+/, '')}</option>
          ))}
        </select>
        <select {...sel} value={f.seniority} onChange={(e) => aggiorna('seniority', e.target.value)}>
          <option value="">Seniority (tutte)</option>
          {(filtri?.seniority || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>

        {descrizione && (
          <button type="button" className="btn-link" onClick={() => { setF(FILTRI_VUOTI); setFApplicati(FILTRI_VUOTI); setOffset(0); }}>
            Azzera filtri
          </button>
        )}
      </div>

      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-ink-muted)', marginBottom: 'var(--space-3)' }}>
        {status === 'success' && (
          <>
            {totale} {totale === 1 ? 'esperienza' : 'esperienze'} · {conteggi.aperta ?? 0} in corso · {conteggi.chiusa ?? 0} chiuse
            {descrizione ? ` — filtri: ${descrizione}` : ''}
          </>
        )}
      </div>

      {status === 'loading' && (
        <div className="card">
          <div className="skeleton-line" style={{ width: '60%' }} />
          <div className="skeleton-line" style={{ width: '80%' }} />
          <div className="skeleton-line" style={{ width: '40%' }} />
        </div>
      )}

      {status === 'error' && (
        <div className="state-error">
          Non è stato possibile caricare le esperienze.
          <div><button type="button" className="btn-link" onClick={refetch}>Riprova</button></div>
          <ErrorDetails error={error} />
        </div>
      )}

      {status === 'success' && righe.length === 0 && (
        <div className="state-empty">
          Nessuna esperienza trovata{descrizione ? ` con i criteri applicati: ${descrizione}` : ''}.
        </div>
      )}

      {status === 'success' && righe.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
            <thead>
              <tr>
                {COLONNE.map(([col, label]) => (
                  <th
                    key={col}
                    onClick={() => ordinaPer(col)}
                    style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', whiteSpace: 'nowrap', color: ordina === col ? 'var(--color-primary)' : 'var(--color-ink-muted)' }}
                  >
                    {label}{ordina === col ? (direzione === 'asc' ? ' ↑' : ' ↓') : ''}
                  </th>
                ))}
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-ink-muted)', textAlign: 'left' }}>Uso commerciale</th>
                <th style={{ borderBottom: '1px solid var(--color-border)' }} />
              </tr>
            </thead>
            <tbody>
              {righe.map((e) => {
                const u = etichettaUtilizzabile(e.utilizzabile);
                const apri = () => navigate(e.scheda_id
                  ? `/knowledge-management/schede/${e.scheda_id}`
                  : `/knowledge-management/esperienze/${e.esperienza_id}`);
                return (
                  <tr key={e.esperienza_id} onClick={apri} style={{ cursor: 'pointer', borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{e.progetto || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{e.cliente || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{e.consulente || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{e.ruolo || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{e.settore || nomeLeggibile(filtri?.settori, e.settore_l1) || '—'}{(e.settore_l2_nome || nomeLeggibile(filtri?.settori, e.settore_l2)) ? ` · ${e.settore_l2_nome || nomeLeggibile(filtri?.settori, e.settore_l2)}` : ''}</td>
                    <td style={{ padding: '10px 12px' }}>{e.dominio || nomeLeggibile(filtri?.domini, e.dominio_l1) || '—'}{(e.dominio_l2_nome || nomeLeggibile(filtri?.domini, e.dominio_l2)) ? ` · ${e.dominio_l2_nome || nomeLeggibile(filtri?.domini, e.dominio_l2)}` : ''}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{e.periodo || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span className={`badge ${e.stato === 'chiusa' ? 'badge-neutral' : 'badge-success'}`}>
                        {e.stato === 'chiusa' ? 'Chiusa' : 'In corso'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{testoUltimaCompilazione(e.ultima_compilazione)}</td>
                    <td style={{ padding: '10px 12px' }}><span className={`badge ${u.classe}`}>{u.testo}</span></td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <Link
                        to={`/knowledge-management/esperienze/${e.esperienza_id}`}
                        className="btn-link"
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        Esperienza →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {status === 'success' && totale > PAGINA && (
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginTop: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
          <button type="button" className="btn btn-secondary" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGINA))}>← Precedenti</button>
          <span>{offset + 1}–{Math.min(offset + PAGINA, totale)} di {totale}</span>
          <button type="button" className="btn btn-secondary" disabled={!data?.ha_altre_pagine} onClick={() => setOffset((o) => o + PAGINA)}>Successive →</button>
        </div>
      )}
    </div>
  );
}
